import { create } from 'zustand';

import {
  listOTRooms,
  createOTRoom as apiCreateOTRoom,
  updateOTRoom as apiUpdateOTRoom,
  deactivateOTRoom as apiDeactivateOTRoom,
  setOTRoomDepartments as apiSetOTRoomDepartments,
} from '../services/api/otRoomApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * OT Room config store (B2 / S6–S7) — Zustand
 *
 * Clone of bedCategoryStore.js keyed by `ot_room_id` (org-global; minimal —
 * name/type/active only, no presets). Idempotent + single-flight fetch —
 * bounded requests (memory rule: runtime safety / no request storms).
 * ═══════════════════════════════════════════════════════════════════════
 */

const useOTRoomStore = create((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────
  otRooms: [],
  isLoading: false,
  error: null,
  includeInactive: false,
  loaded: false,
  _inFlight: null,

  // ── Fetch (bounded: single-flight + loaded guard) ──────────────────
  fetchOTRooms: async ({ force = false } = {}) => {
    const { _inFlight, loaded } = get();
    if (_inFlight) return _inFlight;
    if (loaded && !force) {
      return { success: true, data: { ot_rooms: get().otRooms } };
    }
    set({ isLoading: true, error: null });
    const promise = (async () => {
      const result = await listOTRooms({ include_inactive: get().includeInactive });
      if (result.success) {
        set({
          otRooms: result.data.ot_rooms || [],
          isLoading: false,
          loaded: true,
          _inFlight: null,
        });
      } else {
        set({ error: result.error, isLoading: false, _inFlight: null });
      }
      return result;
    })();
    set({ _inFlight: promise });
    return promise;
  },

  // ── Create → refetch ───────────────────────────────────────────────
  createOTRoom: async (body) => {
    const result = await apiCreateOTRoom(body);
    if (result.success) {
      await get().fetchOTRooms({ force: true });
    }
    return result;
  },

  // ── Update (partial) — patch in place by id ────────────────────────
  updateOTRoom: async (ot_room_id, body) => {
    const result = await apiUpdateOTRoom(ot_room_id, body);
    if (result.success) {
      set((s) => ({
        otRooms: s.otRooms.map((r) =>
          r.ot_room_id === ot_room_id ? { ...r, ...(result.data || body) } : r
        ),
      }));
    }
    return result;
  },

  // ── B5 / S3 — dept tagging ("Visible to", grouping hint only). Patches the
  // row in place with the server's post-flip owner_scope + department_ids. ──
  setOTRoomDepartments: async (ot_room_id, department_ids) => {
    const result = await apiSetOTRoomDepartments(ot_room_id, department_ids);
    if (result.success && result.data) {
      set((s) => ({
        otRooms: s.otRooms.map((r) =>
          r.ot_room_id === ot_room_id
            ? {
                ...r,
                owner_scope: result.data.owner_scope,
                department_ids: result.data.department_ids || [],
              }
            : r
        ),
      }));
    }
    return result;
  },

  // ── Deactivate (soft-delete) ───────────────────────────────────────
  deactivateOTRoom: async (ot_room_id) => {
    const result = await apiDeactivateOTRoom(ot_room_id);
    if (result.success) {
      const { includeInactive } = get();
      set((s) => ({
        otRooms: includeInactive
          ? s.otRooms.map((r) => (r.ot_room_id === ot_room_id ? { ...r, active: false } : r))
          : s.otRooms.filter((r) => r.ot_room_id !== ot_room_id),
      }));
    }
    return result;
  },

  // ── Backend filter: refetch on flip ────────────────────────────────
  setIncludeInactive: (value) => {
    set({ includeInactive: !!value });
    return get().fetchOTRooms({ force: true });
  },

  clearError: () => set({ error: null }),
}));

export default useOTRoomStore;
